import { customAlphabet } from 'nanoid';

/**
 * Configuration options for ID generation
 * @typedef {Object} GenerateIdOptions
 * @property {number} [size=22] - The length of the generated ID
 * @property {string} [alphabet='0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'] - The characters to use in the generated ID - see what nanoid supports here: https://github.com/ai/nanoid?tab=readme-ov-file#custom-alphabet-or-size
 */
export type GenerateIdOptions = {
  size?: number;
  alphabet?: string;
};

/**
 * Generates a random ID string using nanoid with configurable length and character set
 *
 * @param {GenerateIdOptions} options - Configuration options for ID generation
 * @param {number} [options.size=22] - The length of the generated ID
 * @param {string} [options.alphabet='0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'] - The characters to use in the generated ID
 * @returns {string} A randomly generated ID string
 *
 * @example
 * // Generate a default ID (22 characters with alphanumeric alphabet)
 * const id = generateId();
 *
 * @example
 * // Generate a shorter ID with only lowercase letters
 * const shortId = generateId({ size: 10, alphabet: 'abcdefghijklmnopqrstuvwxyz' });
 */
export const generateId = ({
  size = 22,
  alphabet = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
}: GenerateIdOptions = {}): string => customAlphabet(alphabet, size)();
